import { HttpClient, HttpErrorResponse } from '@angular/common/http';
import { Component, computed, inject, OnInit, Signal, signal, WritableSignal } from '@angular/core';
import { FormControl, FormGroup, FormsModule, Validators } from '@angular/forms';
import { RouterLink } from '@angular/router';
import { pathToPrivacyPolicyVersion } from '../../../../constants/app.constants';
import { RequestStateEnum } from '../../../../enums/app.enum';
import { UserService } from '../../../../services/user.service';
import { passwordMatchValidator } from '../../../../validators/password-match.validator';
import { passwordStrengthValidator } from '../../../../validators/password.validator';
import { AlertComponent } from '../../../atoms/alert/alert.component';
import { Alert, AlertTypeEnum } from '../../../atoms/alert/alert.types';
import { CheckboxComponent } from '../../../atoms/checkbox/checkbox.component';
import { DropdownComponent } from '../../../atoms/dropdown/dropdown.component';
import { InputComponent } from '../../../atoms/input/input.component';
import { PasswordInputComponent } from '../../../atoms/password-input/password-input.component';
import { AuthButtonComponent } from '../../../molecules/auth-button/auth-button.component';
import { AuthTemplateComponent } from '../../../organisms/auth-template/auth-template.component';
import { DataPrivacyPolicy } from '../data-privacy/data-privacy.interface';
import { messages, selectOptions } from './register.constants';

@Component({
  selector: 'app-register',
  templateUrl: './register.component.html',
  imports: [
    AuthTemplateComponent,
    FormsModule,
    InputComponent,
    DropdownComponent,
    AuthButtonComponent,
    CheckboxComponent,
    RouterLink,
    AlertComponent,
    PasswordInputComponent,
  ],
})
export class RegisterComponent implements OnInit {
  protected RequestStateEnum = RequestStateEnum;

  public alert = signal<Alert | null>(null);
  public versionPricacyPolicy = '';
  public userService = inject(UserService);
  public httpClient = inject(HttpClient);
  public requestState: WritableSignal<RequestStateEnum | null> = signal(null);
  public description: Signal<string> = computed(() => this.updateDescription());
  public options = selectOptions;
  public form = new FormGroup(
    {
      email: new FormControl(null, [Validators.required, Validators.email]),
      password: new FormControl(null, [Validators.required, passwordStrengthValidator()]),
      confirmPassword: new FormControl(null, [Validators.required]),
      firstname: new FormControl(null, [Validators.required]),
      lastname: new FormControl(null, [Validators.required]),
      gender: new FormControl(null, [Validators.required]),
      privacyPolicy: new FormControl(false, [Validators.requiredTrue]),
    },
    {
      validators: passwordMatchValidator('password', 'confirmPassword'),
    }
  );

  ngOnInit(): void {
    this.getPrivacyPolicyVesion();
  }

  private getPrivacyPolicyVesion(): void {
    this.httpClient
      .get<DataPrivacyPolicy>(pathToPrivacyPolicyVersion)
      .subscribe((response) => (this.versionPricacyPolicy = response.version));
  }

  private updateDescription(): string {
    switch (this.requestState()) {
      case RequestStateEnum.PENDING:
        return messages.pending!;
      case RequestStateEnum.ERROR:
        return messages.error!;
      case RequestStateEnum.SUCCESS:
        return messages.success!;
      default:
        return messages.default!;
    }
  }

  public get passwordConformErrorMsg(): string {
    const errors = this.form.controls.confirmPassword.errors;

    if (errors && errors['passwordMismatch']) {
      return 'Passwörter stimmen nicht überein';
    }
    return '';
  }

  public register(): void {
    this.requestState.set(RequestStateEnum.PENDING);
    this.form.disable();

    const fc = this.form.controls;
    this.userService
      .register(
        fc.email.value!,
        fc.password.value!,
        fc.firstname.value!,
        fc.lastname.value!,
        fc.gender.value!,
        this.versionPricacyPolicy
      )
      .subscribe({
        next: () => this.requestState.set(RequestStateEnum.SUCCESS),
        error: (error: HttpErrorResponse) => {
          if (error.status === 409) {
            this.requestState.set(null);
            this.form.enable();
            this.form.markAsPristine();

            this.alert.set({
              message: 'Diese E-mail Adresse ist bereits registiert.',
              type: AlertTypeEnum.ERROR,
            });
          } else {
            this.requestState.set(RequestStateEnum.ERROR);
          }
        },
      });
  }
}
